import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from "antd";
import { useState } from "react";
import { validateAddress } from "../../../components/apiRepository/GeocoderRepository";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import { validatePhoneNumber } from "../../../components/validation/validatePhoneNumber";
import states from "../../../constants/states";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import useComponentSize from "../../../hooks/useComponentSize";

const { Title, Paragraph } = Typography;

export default function StoreInformation({ proceed, back }) {
  const dispatch = useStoreDispatch();
  const { store } = useStore();
  const [form] = Form.useForm();
  const watchStoreName = Form.useWatch("name", form);
  const watchLegalCorporationName = Form.useWatch("legalCorporationName", form);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [invalidAddress, setInvalidAddress] = useState(false);
  const componentSize = useComponentSize();

  const onFinish = async (values) => {
    if (invalidAddress) return;

    setLoading(true);

    try {
      const res = await validateAddress(values?.address);

      if (!res?.isAddressValid) {
        setInvalidAddress(true);
        return;
      }

      dispatch({ type: ACTIONTYPES.STORE, data: values });
      proceed();
    } catch (err) {
      console.error("Address validation failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleCheckboxSameAsStore = (e) => {
    if (e.target.checked) {
      setChecked(true);
      form.setFieldValue("legalCorporationName", watchStoreName);
    } else {
      setChecked(false);
    }
  };

  const handleStoreNameOnChange = () => {
    if (checked) {
      form.setFieldValue("legalCorporationName", watchStoreName);
    }
  };

  const handleLegalNameChanged = () => {
    if (watchStoreName !== watchLegalCorporationName && checked) {
      setChecked(false);
    }
  };

  const clearAddressError = () => {
    setInvalidAddress(false);
  };

  return (
    <Row className="BevzForm">
      <Col
        xs={{ span: 22, offset: 1 }}
        lg={{ span: 20, offset: 2 }}
        xl={{ span: 15, offset: 2 }}
      >
        <Title
          level={componentSize === "default" ? 4 : 2}
          className="BevzFormHeader"
        >
          Store Information
        </Title>
        <Paragraph className="BevzFormSubHeader">
          Make sure to provide correct details about your store.
        </Paragraph>

        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          style={{ marginTop: "2.5rem", width: "100%" }}
          initialValues={store}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label={
              <>
                Store Name<span style={{ color: "red" }}>*</span>
              </>
            }
            required
            rules={[{ required: true, message: "This is a required field." }]}
          >
            <Input
              placeholder=""
              size="large"
              onKeyUp={handleStoreNameOnChange}
            />
          </Form.Item>

          <Form.Item
            name="legalCorporationName"
            label={
              <>
                Legal Corporation Name<span style={{ color: "red" }}>*</span>
              </>
            }
            required
            rules={[{ required: true, message: "This is a required field." }]}
            style={{ marginBottom: "5px" }}
          >
            <Input
              placeholder=""
              size="large"
              onKeyUp={handleLegalNameChanged}
            />
          </Form.Item>

          <Checkbox
            checked={checked}
            onChange={handleCheckboxSameAsStore}
            style={{ fontSize: "14px", color: "#00000065" }}
          >
            Same as Store Name
          </Checkbox>

          <Row gutter={30} style={{ marginBottom: 0, marginTop: "24px" }}>
            <Col xs={24} md={12}>
              <Form.Item
                name="emailAddress"
                label={
                  <>
                    Store Email Address<span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    type: "email",
                    message: "Please enter valid email address.",
                  },
                ]}
              >
                <Input placeholder="store@email.com" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="phoneNumber"
                label={
                  <>
                    Main Store Phone Number
                    <span style={{ color: "red" }}>*</span>
                  </>
                }
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      const res =
                        value.length >= 4 && validatePhoneNumber(value);

                      if (res.isValid === false) {
                        return Promise.reject(new Error(res.error));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value.replace(/[^0-9\s\-\+\(\)]/g, "");
                  const res = validatePhoneNumber(cleaned);
                  return res.isValid ? res.formatted : cleaned;
                }}
              >
                <Input
                  type="text"
                  placeholder="(000) 000-0000"
                  size="large"
                  maxLength={25}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <>
                Store Address
                <span style={{ color: "red" }}>
                  *{" "}
                  {invalidAddress
                    ? "Please check your store address. City, state, or ZIP code may not match."
                    : null}
                </span>
              </>
            }
            required
            style={{ marginBottom: 0, marginTop: "6px" }}
          >
            <Form.Item
              name={["address", "street1"]}
              required
              rules={[{ required: true, message: "This is a required field." }]}
            >
              <Input
                placeholder="Address Line 1"
                size="large"
                onChange={clearAddressError}
              />
            </Form.Item>

            <Row gutter={30}>
              <Col xs={24} md={15}>
                <Form.Item name={["address", "street2"]}>
                  <Input placeholder="Address Line 2 (optional)" size="large" />
                </Form.Item>
              </Col>
              <Col xs={12} md={9}>
                <Form.Item
                  name={["address", "city"]}
                  required
                  rules={[
                    { required: true, message: "This is a required field." },
                  ]}
                >
                  <Input
                    placeholder="City"
                    size="large"
                    onChange={clearAddressError}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name={["address", "state"]}
                  required
                  rules={[
                    { required: true, message: "This is a required field." },
                  ]}
                >
                  <Select
                    size="large"
                    options={states}
                    placeholder="State"
                    onChange={clearAddressError}
                  />
                </Form.Item>
              </Col>

              <Col xs={12} md={8}>
                <Form.Item
                  name={["address", "zipCode"]}
                  required
                  rules={[
                    { required: true, message: "This is a required field." },
                    {
                      pattern: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
                      message: "Please enter 5-digit zip code.",
                    },
                  ]}
                  normalize={(value) => {
                    if (!value) return value;
                    return value.replace(/\D/g, "");
                  }}
                >
                  <Input
                    placeholder="ZIP Code"
                    size="large"
                    maxLength={5}
                    onChange={clearAddressError}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Input size="large" disabled defaultValue="United States" />
              </Col>
            </Row>
          </Form.Item>

          <Form.Item
            label={
              <>
                Store Sales Tax %<span style={{ color: "red" }}>*</span>
              </>
            }
            required
            style={{ marginTop: "6px" }}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="tax"
                  rules={[
                    { required: true, message: "This is a required field." },
                    {
                      validator: (_, value) => {
                        if (value && +value > 100) {
                          return Promise.reject(
                            new Error(
                              "Please enter a value that is 100 or less.",
                            ),
                          );
                        }

                        if (value && +value <= 0) {
                          return Promise.reject(
                            new Error(
                              "Please enter a value that is greater than 0",
                            ),
                          );
                        }

                        if (value && String(value).trim() === ".") {
                          return Promise.reject(
                            new Error(
                              "Please enter a value that is greater than 0",
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                  normalize={(value) => {
                    if (!value) return value;
                    const cleaned = value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*?)\..*/g, "$1");
                    let truncated = cleaned.slice(0, cleaned.indexOf(".") + 4);
                    return truncated;
                  }}
                >
                  <Input
                    placeholder="0.000"
                    suffix={"%"}
                    size="large"
                    maxLength={6}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          <Row
            align="middle"
            justify="space-between"
            className="BevzFormButtonSection"
          >
            <Col>
              <Form.Item>
                <Button
                  size="large"
                  type="default"
                  shape="round"
                  icon={<ArrowLeftOutlined />}
                  onClick={back}
                >
                  Back
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <BrandButton
                  type="solidyellow"
                  htmlType="submit"
                  // ref={submitButtonRef}
                  // onSubmit={handleSubmit}
                  loading={loading}
                  size="large"
                >
                  {componentSize === "default"
                    ? "Proceed"
                    : "Proceed to Contact Information"}
                </BrandButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}
